// START IMPORTS
import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import {
  ExperimentHeader,
  StandardSearchPage,
} from '@ltvco/refresh-lib/experiments/RFRSH-4335';
import { Box, Dialog } from '@ltvco/refresh-lib/theme';
import { useSession } from '@ltvco/refresh-lib/v1';
import {
  Header,
  HeaderMenu,
  Lang,
  MenuToggle,
  NotificationBell,
  NotificationDropdown,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import React, { useEffect, useState } from 'react';

import { MobileDownloadSnackbar } from 'components/MobileDownloadSnackbar/MobileDownloadSnackbar';

// END IMPORTS

// START INTERFACES_AND_TYPES
type HeaderParentProps = {
  handleSearchSuccess: any;
  changeSingleReportVariation: (variation: boolean) => void;
  changeOwnerSearchVariation: (variation: boolean) => void;
  changeIsInAstrologyStandaloneVariation: (variation: boolean) => void;
  changeInternationalLanguageTest: (variation: boolean) => void;
  changeInternationalVinTest: (variation: boolean) => void;
  changeListV2Variation: (variation: boolean) => void;
  changeChatBVFlowFooterVariation: (variation: boolean) => void;
};
// END INTERFACES_AND_TYPES

export const HeaderParent = ({
  handleSearchSuccess,
  changeSingleReportVariation,
  changeIsInAstrologyStandaloneVariation,
  changeInternationalLanguageTest,
  changeInternationalVinTest,
  changeListV2Variation,
  changeChatBVFlowFooterVariation,
}: HeaderParentProps) => {
  const {
    session: { account },
  } = useSession();

  // RFRSH-4335: Flag check lives here in BV, not in the lib
  const isInStandardSearchDesign = useFeatureIsOn('RFRSH-4335');
  const [isSearchPageOpen, setIsSearchPageOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);

  const isInAstrologyStandaloneVariation = useFeatureIsOn('oar-1164');
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const isOnChatBVFlowFooterVariation = useFeatureIsOn('prop-3640');
  const UKFraudScanVariation = useFeatureIsOn('RTRN-1565');

  const {
    singleReportVariation,
    internationalLanguageVariation,
    vinVariation,
  } = useSubscriptionAccessConfigContext();
  // END HOOKS_AND_QUERIES

  // START EFFECTS
  useEffect(() => {
    changeSingleReportVariation(singleReportVariation);
  }, [singleReportVariation]);

  useEffect(() => {
    changeIsInAstrologyStandaloneVariation(isInAstrologyStandaloneVariation);
  }, [isInAstrologyStandaloneVariation]);

  useEffect(() => {
    changeInternationalLanguageTest(internationalLanguageVariation);
  }, [internationalLanguageVariation]);

  useEffect(() => {
    changeInternationalVinTest(vinVariation);
  }, [vinVariation]);

  useEffect(() => {
    changeListV2Variation(isInListV2Variation);
  }, [isInListV2Variation]);

  useEffect(() => {
    changeChatBVFlowFooterVariation(isOnChatBVFlowFooterVariation);
  }, [isOnChatBVFlowFooterVariation]);

  if (location.pathname.includes('/chatbv')) {
    return null;
  }
  // END EARLY_RETURNS

  // START RENDER
  const spanishPlanEnabled =
    account?.account?.language_info?.plan_language === 'es';
  const portuguesePlanEnabled =
    account?.account?.language_info?.plan_language === 'pt';
  const signupLanguage =
    account?.account?.language_info?.browser_language ?? 'en';

  /** Dev only: VITE_DEV_SHOW_TRANSLATE_WIDGET=true|1 shows translate UI without a Spanish/PT plan. */
  const devTranslateFlag = (
    import.meta.env.VITE_DEV_SHOW_TRANSLATE_WIDGET ?? ''
  ).toLowerCase();
  const devForceTranslateWidget =
    import.meta.env.DEV &&
    (devTranslateFlag === 'true' || devTranslateFlag === '1');

  const translationLanguages = devForceTranslateWidget
    ? ['es']
    : (UKFraudScanVariation && signupLanguage !== 'en') ||
      spanishPlanEnabled ||
      portuguesePlanEnabled
    ? ['es', 'pt']
    : undefined;

  const browserLang = globalThis.navigator?.language?.slice(0, 2);
  const defaultLang = translationLanguages?.includes(browserLang as Lang)
    ? browserLang
    : 'en';

  // RFRSH-4335: Render experiment header (no canon Header changes needed)
  if (isInStandardSearchDesign) {
    return (
      <Box>
        <ExperimentHeader
          onSearchClick={() => setIsSearchPageOpen(true)}
          rightContent={
            <>
              <NotificationBell
                unreadCount={unreadNotificationCount}
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  setNotificationAnchorEl(e.currentTarget)
                }
              />
              <MenuToggle toggleAction={() => setIsMenuOpen(true)} />
            </>
          }
        />
        <HeaderMenu
          open={isMenuOpen}
          handleClose={() => setIsMenuOpen(false)}
          customStyle={{ width: 284 }}
        />
        <NotificationDropdown
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          handleClose={() => setNotificationAnchorEl(null)}
          setUnreadNotificationCount={setUnreadNotificationCount}
        />
        <Dialog
          open={isSearchPageOpen}
          fullScreen
          PaperProps={{ sx: { bgcolor: 'white' } }}
        >
          <StandardSearchPage
            onSearchSuccess={(data: any) => {
              handleSearchSuccess(data);
              setIsSearchPageOpen(false);
            }}
            onBack={() => setIsSearchPageOpen(false)}
            placement="header"
            searchFrom="search_header"
          />
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <MobileDownloadSnackbar />
      <Header
        onSearchSuccess={handleSearchSuccess}
        translationOptions={{
          translationLanguages: translationLanguages as Lang[],
          defaultMountLanguage: defaultLang,
        }}
      />
    </Box>
  );
  // END RENDER
};
